import {
  PERSONAL_SETTINGS,
  WORKSPACE_PAGES,
  WORKSPACE_SETTINGS,
  type PageDefinition,
} from '@/convex/products/ai/navigation/page_catalog_definitions.helpers';
import { boundedRealtimeVoiceContextText as boundedText } from '@/lib/realtime-voice-context-text';

const MAX_TITLE_LENGTH = 120;
const MAX_SUMMARY_LENGTH = 320;
const MAX_OPTION_LENGTH = 80;
const MAX_VISIBLE_OPTIONS = 8;
const MAX_THREAD_ID_LENGTH = 128;

const WORKSPACE_SECTIONS = {
  aesthetics: 'Aesthetics',
  assets: 'Assets',
  avatars: 'Avatars',
  bash: 'Workspace terminal',
  branding: 'Brands',
  chats: 'Channels',
  connectors: 'Connectors',
  dashboard: 'Dashboard',
  design: 'Design',
  documents: 'Documents',
  downloads: 'Downloads',
  drives: 'My Drive',
  edit: 'Editor',
  explore: 'Explore',
  extensions: 'Extensions',
  files: 'Files',
  imai: 'Your IMAI',
  library: 'Library',
  products: 'Products',
  projects: 'Projects',
  references: 'References',
  scheduled: 'Scheduled work',
  settings: 'Workspace settings',
  studio: 'Studio',
  support: 'Support',
  techstudio: 'Tech Studio',
  threads: 'Chats',
  trash: 'Trash',
  'trend-analysis': 'Trend analysis',
} as const;

export type RealtimeVoicePageMetadata = {
  /** The page must provide the pathname it rendered for. */
  pathname: string;
  title?: string | null;
  activeView?: string | null;
  selectedItem?: string | null;
  visibleOptions?: readonly string[];
  /** Supply only after the viewed chat has passed its normal access checks. */
  viewedThread?: {
    id: string;
    title?: string | null;
    summary?: string | null;
  } | null;
};

export type RealtimeVoicePageContext = {
  sourceVoiceThreadId: string;
  page: {
    scope: 'workspace' | 'personal';
    section: string;
    title?: string;
    activeView?: string;
    selectedItem?: string;
    visibleOptions?: string[];
  };
  viewedThread?: {
    id: string;
    title?: string;
    summary?: string;
  };
};

function validThreadId(value: string): boolean {
  return (
    value.length > 0 &&
    value.length <= MAX_THREAD_ID_LENGTH &&
    /^[A-Za-z0-9_-]+$/.test(value)
  );
}

function pathnameSegments(pathname: string): string[] | null {
  if (
    !pathname.startsWith('/') ||
    pathname.includes('?') ||
    pathname.includes('#')
  ) {
    return null;
  }
  return pathname.split('/').filter(Boolean);
}

function catalogPageLabel(
  route: string,
  definitions: readonly PageDefinition[]
): string | null {
  let match: PageDefinition | null = null;
  for (const definition of definitions) {
    if (
      (definition.route === '' && route === '') ||
      (definition.route !== '' &&
        (route === definition.route ||
          route.startsWith(`${definition.route}/`)))
    ) {
      if (!match || definition.route.length > match.route.length) {
        match = definition;
      }
    }
  }
  return match?.label ?? null;
}

function pageFacts(
  scope: 'workspace' | 'personal',
  section: string,
  metadata: RealtimeVoicePageMetadata | null
): RealtimeVoicePageContext['page'] {
  const page: RealtimeVoicePageContext['page'] = { scope, section };
  const title = boundedText(metadata?.title, MAX_TITLE_LENGTH);
  const activeView = boundedText(metadata?.activeView, MAX_TITLE_LENGTH);
  const selectedItem = boundedText(metadata?.selectedItem, MAX_TITLE_LENGTH);
  const visibleOptions = metadata?.visibleOptions
    ?.slice(0, MAX_VISIBLE_OPTIONS)
    .map((option) => boundedText(option, MAX_OPTION_LENGTH))
    .filter((option): option is string => Boolean(option));
  if (title) page.title = title;
  if (activeView) page.activeView = activeView;
  if (selectedItem) page.selectedItem = selectedItem;
  if (visibleOptions?.length) page.visibleOptions = visibleOptions;
  return page;
}

/** Build a small snapshot from route structure and page-owned, already-authorized facts. */
export function buildRealtimeVoicePageContext({
  pathname,
  sourceOrgSlug,
  sourceThreadId,
  workspaceUsername,
  pageMetadata,
}: {
  pathname: string;
  sourceOrgSlug: string;
  sourceThreadId: string;
  /** Authenticated member's workspace username, if available. */
  workspaceUsername?: string | null;
  pageMetadata?: RealtimeVoicePageMetadata | null;
}): RealtimeVoicePageContext | null {
  const segments = pathnameSegments(pathname);
  if (!segments || !validThreadId(sourceThreadId)) return null;

  const metadata = pageMetadata?.pathname === pathname ? pageMetadata : null;
  if (segments[0] === 'settings' || pathname === '/shared') {
    const section =
      catalogPageLabel(pathname, PERSONAL_SETTINGS) ?? 'Personal settings';
    return {
      sourceVoiceThreadId: sourceThreadId,
      page: pageFacts('personal', section, metadata),
    };
  }

  if (segments[0] !== sourceOrgSlug || !sourceOrgSlug) return null;
  const sectionKey = segments[1] ?? '';
  const route = segments.length > 1 ? `/${segments.slice(1).join('/')}` : '';
  const isOwnHome =
    sectionKey === 'files' &&
    segments[2] === 'users' &&
    !!workspaceUsername &&
    segments[3] === `@${workspaceUsername}`;
  const section = isOwnHome
    ? 'My Drive'
    : (catalogPageLabel(route, [...WORKSPACE_PAGES, ...WORKSPACE_SETTINGS]) ??
      (Object.prototype.hasOwnProperty.call(WORKSPACE_SECTIONS, sectionKey)
        ? WORKSPACE_SECTIONS[sectionKey as keyof typeof WORKSPACE_SECTIONS]
        : null));
  if (!section) return null;

  const page = pageFacts('workspace', section, metadata);

  const viewedThread = metadata?.viewedThread;
  const isViewedThread =
    sectionKey === 'threads' &&
    segments.length === 3 &&
    viewedThread != null &&
    viewedThread.id === segments[2] &&
    validThreadId(viewedThread.id);

  return {
    sourceVoiceThreadId: sourceThreadId,
    page,
    ...(isViewedThread && viewedThread
      ? {
          viewedThread: {
            id: viewedThread.id,
            ...(boundedText(viewedThread.title, MAX_TITLE_LENGTH) && {
              title: boundedText(viewedThread.title, MAX_TITLE_LENGTH),
            }),
            ...(boundedText(viewedThread.summary, MAX_SUMMARY_LENGTH) && {
              summary: boundedText(viewedThread.summary, MAX_SUMMARY_LENGTH),
            }),
          },
        }
      : {}),
  };
}

/** Structured data for the live session and the source thread's GA handoff. */
export function formatRealtimeVoicePageContext(
  context: RealtimeVoicePageContext
): string {
  return JSON.stringify(context);
}
